import React, { useMemo } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { COLOR, FUENTE } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_EMBUDO } from './mockDataEjemplos';
import { calcularPasosEmbudo } from './calculosReportes';

/**
 * Embudo del turno en 3 pasos: Turnos del período → Se presentaron → Trámite abierto, con la caída
 * absoluta y porcentual en cada uno, y debajo cómo se reparten los turnos (se presentaron, no vinieron,
 * cancelaron, sin resolver). No es un gráfico de embudo de librería: unas tarjetas se leen mejor y
 * responden "dónde se pierde" directamente. El cálculo de los pasos vive en calculosReportes.js porque
 * la exportación a PDF/Excel imprime el mismo embudo.
 *
 * Cobros, turnos médicos, ANSES y quien vino por un trámite en curso ya no están en ningún paso: el
 * backend los saca de todas las estadísticas. Por eso la caída del último paso es 100 − conversión.
 * @param {{agendado: number, presentaron: number, conTramite: number, noAsistio?: number,
 *   cancelados?: number}} props
 */
const EmbudoTurnos = ({ agendado, presentaron, conTramite, noAsistio = null, cancelados = null, compacto = false }) => {
    const pasos = useMemo(
        () => calcularPasosEmbudo({ agendado, presentaron, conTramite }),
        [agendado, presentaron, conTramite]
    );
    // Lo que falta para cerrar la cuenta: turnos del día que todavía no se resolvieron, o turnos
    // viejos que quedaron sin cerrar.
    const sinResolver = Math.max(0, (agendado || 0) - (presentaron || 0) - (noAsistio || 0) - (cancelados || 0));

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Embudo del turno"
                        queMuestra="Te muestra, en tres pasos, dónde se va perdiendo gente entre que se agenda un turno y el estudio termina abriendo un trámite — para saber en qué escalón conviene actuar primero."
                        ejemploVisual={<EmbudoTurnos compacto agendado={MOCK_EMBUDO.agendado} presentaron={MOCK_EMBUDO.presentaron} conTramite={MOCK_EMBUDO.conTramite} />}
                        comoLeerlo={[
                            'Cada tarjeta es un paso del proceso, en orden: Turnos del período → Se presentaron → Trámite abierto (el último, en negro, es la meta final).',
                            '"Se presentaron" son los turnos en que la persona vino: los que ya se atendieron y los que están en sala.',
                            'No cuentan los turnos que no son atención al público (cobros, turnos médicos, ANSES...) ni quien vino por un trámite que ya tenía en curso: esa gente no venía a abrir nada. Por eso la caída del último paso es 100 − la conversión del encabezado.',
                            'El texto rojo debajo de cada tarjeta (salvo la primera) es cuánto se perdió respecto al paso anterior, en cantidad y en %.',
                            'Debajo del embudo se reparten los turnos del período: cuántos se presentaron, no vinieron, cancelaron o siguen sin resolver.',
                        ]}
                        analisis="De 90 turnos se presentaron 62 (se pierde 31%), y de esos abrieron un trámite 23 (se pierde 63%, o sea una conversión de 37%). El cuello de botella no es que la gente falte al turno, sino que aun atendiéndola más de la mitad no abre un trámite — vale más revisar qué pasa en esa conversación que insistir con recordatorios de asistencia."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Embudo del turno</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '16px' }}>
                        Cuántos de los turnos del período se presentaron y en cuántos se abrió un trámite.
                    </Typography>
                </>
            )}
            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '4px' }}>
                {pasos.map((p, i) => {
                    const esUltimo = i === pasos.length - 1;
                    return (
                        <React.Fragment key={p.titulo}>
                            {i > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, color: COLOR.grisFrio }}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                                        <path d="M2 8h10M9 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </Box>
                            )}
                            <Box sx={{
                                flex: 1,
                                background: esUltimo ? COLOR.negro : '#F8FAFC',
                                border: esUltimo ? 'none' : '1px solid #F1F5F9',
                                borderRadius: '10px', padding: '14px',
                            }}>
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 24, fontWeight: 700, color: esUltimo ? '#fff' : COLOR.negro, fontVariantNumeric: 'tabular-nums' }}>
                                    {p.valor.toLocaleString('es-AR')}
                                </Typography>
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 11.5, fontWeight: 600, color: esUltimo ? '#ADB5C1' : COLOR.grisTexto, marginTop: '2px' }}>
                                    {p.titulo}
                                </Typography>
                                {p.perdida !== null && (
                                    <Typography sx={{ fontFamily: FUENTE, fontSize: 11, color: COLOR.rojo, marginTop: '6px' }}>
                                        −{p.perdida.toLocaleString('es-AR')} ({p.caidaPct.toFixed(1)}%)
                                    </Typography>
                                )}
                            </Box>
                        </React.Fragment>
                    );
                })}
            </Box>
            {!compacto && (noAsistio !== null || cancelados !== null) && (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 12, color: COLOR.grisTexto, marginTop: '8px' }}>
                    Los {(agendado || 0).toLocaleString('es-AR')} turnos del período: {(presentaron || 0).toLocaleString('es-AR')} se presentaron,
                    {' '}{(noAsistio || 0).toLocaleString('es-AR')} no vinieron y {(cancelados || 0).toLocaleString('es-AR')} cancelaron
                    {sinResolver > 0 ? `, y ${sinResolver.toLocaleString('es-AR')} siguen sin resolver (turnos de hoy o que quedaron sin cerrar)` : ''}.
                </Typography>
            )}
        </Card>
    );
};

export default EmbudoTurnos;
